import { useEffect, useMemo, useState } from "react";
import { useParams, Navigate, useSearchParams } from "react-router-dom";
import api, { formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { MODULE_CATALOG, moduleAccessibleFor, moduleCanDo } from "../lib/modules";
import { useEditAccessRequest } from "../components/EditAccessRequest";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { Plus, Search, Pencil, Trash2, Users } from "lucide-react";
import { PageHeader } from "../components/GradientCard";
import { useGlobalViewMode } from "../components/ViewModeToggle";

// Map module group → tone for the unified gradient design language.
const MODULE_TONE_MAP = {
  HR: "emerald",
  Sales: "sky",
  Documentation: "violet",
  Accounts: "indigo",
  Dispatch: "amber",
  IT: "blue",
  CRM: "sky",
  Trainers: "emerald",
  Workspace: "blue",
  Other: "blue",
};
import OrderWorkflow from "./OrderWorkflow";
import TravelBooking from "./TravelBooking";
import Travel from "./Travel";
import DailyExpense from "./DailyExpense";
import TravelAdvanceActions from "../components/TravelAdvanceActions";
import DispatchWorkflow from "./DispatchWorkflow";
import CRMClients from "./CRMClients";
import CRMReporting from "./CRMReporting";
import GeMWork from "./GeMWork";
import Chat from "./Chat";
import Quotation from "./Quotation";
import AssetsWorkflow from "./AssetsWorkflow";
import Documents from "./Documents";
import Trainers from "./Trainers";
import Documentation from "./Documentation";
import Announcements from "./Announcements";
import Incentive from "./Incentive";
import Holidays from "./Holidays";
import Payroll from "./Payroll";
import VendorManagement from "./VendorManagement";
import ProductIssuance from "./ProductIssuance";
import TeamAttendance from "./TeamAttendance";
import HR from "./HR";
import LeaveManagement from "./LeaveManagement";
import DailyReport from "./DailyReport";

export default function ModulePage() {
  const { moduleKey } = useParams();
  const { user } = useAuth();
  const config = MODULE_CATALOG[moduleKey];

  if (!config) return <Navigate to="/" replace />;
  if (!moduleAccessibleFor(user, moduleKey)) return <Navigate to="/" replace />;

  // Modules that have a dedicated top-level route (e.g. /salary-hike) should
  // bounce to that route instead of falling through to RecordsPage.
  if (config.customRoute) return <Navigate to={config.customRoute} replace />;

  // v109.9.122 — External-type modules (e.g. `my_sales` → `/my-sales`) have
  // no `fields[]` schema; falling through to RecordsPage crashes at
  // `emptyFor(config.fields)`. Redirect to their dedicated route instead.
  if (config.type === "external" && config.href) {
    return <Navigate to={config.href} replace />;
  }

  if (config.type === "workflow-orders") return <OrderWorkflow />;
  if (config.type === "workflow-invoices") return <OrderWorkflow initialTab="invoice" />;
  if (config.type === "workflow-dispatches") return <DispatchWorkflow />;
  if (config.type === "workflow-crm") return <CRMClients />;
  if (config.type === "workflow-crm-reporting") return <CRMReporting />;
  if (config.type === "workflow-gem-work") return <GeMWork />;
  if (config.type === "workflow-chat") return <Chat />;
  if (config.type === "workflow-quotation") return <Quotation />;
  if (config.type === "workflow-travel-booking") return <TravelBooking />;
  if (config.type === "workflow-travel") return <Travel />;
  if (config.type === "workflow-daily-expense") return <DailyExpense />;
  if (config.type === "workflow-assets") return <AssetsWorkflow />;
  if (config.type === "workflow-documents") return <Documents />;
  if (config.type === "workflow-trainers") return <Trainers />;
  if (config.type === "workflow-documentation") return <Documentation />;
  if (config.type === "workflow-announcements") return <Announcements />;
  if (config.type === "workflow-incentive") return <Incentive />;
  if (config.type === "workflow-holidays") return <Holidays />;
  if (config.type === "workflow-payroll") return <Payroll />;
  if (config.type === "workflow-vendors") return <VendorManagement />;
  if (config.type === "workflow-product-issuance") return <ProductIssuance />;
  if (config.type === "workflow-team-attendance") return <TeamAttendance />;
  if (config.type === "workflow-hr") return <HR />;
  if (config.type === "workflow-leaves") return <LeaveManagement />;
  if (config.type === "workflow-daily-report") return <DailyReport />;

  return <RecordsPage key={moduleKey} moduleKey={moduleKey} config={config} />;
}

function emptyFor(fields, user) {
  const today = new Date().toISOString().slice(0, 10);
  const now = new Date().toTimeString().slice(0, 5); // HH:MM
  // v109.9.122 — Defensive: some catalog entries (external / customRoute)
  // legitimately have no `fields[]`. Return {} rather than crashing.
  if (!Array.isArray(fields)) return {};
  return Object.fromEntries(fields.map((f) => {
    if (f.autoDate) return [f.key, today];
    if (f.autoTime) return [f.key, now];
    if (f.autoFromUser && user) return [f.key, user[f.autoFromUser] || ""];
    if (f.defaultValue) return [f.key, f.defaultValue];
    if (f.type === "date" && (f.key === "date" || f.key === "invoice_date" || f.key === "dispatch_date")) {
      return [f.key, today];
    }
    return [f.key, ""];
  }));
}

function calcDays(from, to) {
  if (!from || !to) return "";
  const a = new Date(from); const b = new Date(to);
  if (isNaN(a) || isNaN(b)) return "";
  const ms = b - a;
  if (ms < 0) return "";
  return Math.floor(ms / (24 * 60 * 60 * 1000)) + 1;
}

export function RecordsPage({ moduleKey, config, embedded = false, actionPulse = null }) {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [records, setRecords] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyFor(config.fields, user));
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [leadFilter, setLeadFilter] = useState("all");
  const [followFilter, setFollowFilter] = useState("all");
  const [dayFilter, setDayFilter] = useState("all"); // for hr_daily_report: all | today | week | custom
  const [customDate, setCustomDate] = useState("");

  // v109.9.55 — Dashboard quick-actions may deep-link with `?action=new` to
  // auto-open the New Record dialog for this module (e.g. Apply Leave).
  useEffect(() => {
    if (searchParams.get("action") === "new") {
      setEditingId(null);
      setForm(emptyFor(config.fields, user));
      setOpen(true);
      const next = new URLSearchParams(searchParams);
      next.delete("action");
      setSearchParams(next, { replace: true });
    }
     
  }, [searchParams, moduleKey]);

  // v109.9.56 — Prop-based actionPulse (opened as modal from Dashboard).
  useEffect(() => {
    if (actionPulse && actionPulse.key === "new") {
      setEditingId(null);
      setForm(emptyFor(config.fields, user));
      setOpen(true);
    }
     
  }, [actionPulse]);
  // v109.7 — it_tickets honours the global Card/List view toggle.
  const GRANT_LIST_MODULES = ["it_tickets"];
  const supportsViewToggle = GRANT_LIST_MODULES.includes(moduleKey);
  // v109.9.7 — Universal save-and-request wiring for extended modules.
  // Approve → backend auto-applies payload; reject → nothing applied.
  const { dialog: editReqDialog, wrapMutation: wrapEditRequest } = useEditAccessRequest();
  const [globalViewMode] = useGlobalViewMode();
  const viewMode = supportsViewToggle ? globalViewMode : "list";

  const load = async () => {
    try {
      const { data } = await api.get(`/records/${moduleKey}`);
      setRecords(data);
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    }
  };

  useEffect(() => { load(); setForm(emptyFor(config.fields, user)); setEditingId(null); }, [moduleKey]); // eslint-disable-line

  const openCreate = () => {
    setForm(emptyFor(config.fields, user));
    setEditingId(null);
    setOpen(true);
  };

  const openEdit = (rec) => {
    setForm({ ...emptyFor(config.fields, user), ...rec });
    setEditingId(rec.id);
    setOpen(true);
  };

  const submit = async () => {
    // Validate required
    for (const f of config.fields) {
      if (f.required && !String(form[f.key] || "").trim()) {
        toast.error(`${f.label} is required`);
        return;
      }
    }
    setLoading(true);
    try {
      const body = Object.fromEntries(config.fields.map((f) => [f.key, form[f.key] ?? ""]));
      if (editingId) {
        // v109.9.7 — auto-stage via approval request if backend returns 403
        // (e.g. user has view-only grant / no edit permission on this record).
        const result = await wrapEditRequest(
          () => api.patch(`/records/${moduleKey}/${editingId}`, body),
          {
            moduleKey,
            recordId: editingId,
            recordLabel: form.title || form.name || form.subject || editingId,
            proposedPayload: body,
            stageOn403: true,
          },
        );
        if (!result) { setOpen(false); setLoading(false); return; }
        toast.success("Updated");
      } else {
        await api.post(`/records/${moduleKey}`, body);
        toast.success("Created");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const remove = async (id) => {
    // v109.9.78 — Universal "approved records are immutable" rule. If the
    // record has been approved (any module) and the current user isn't
    // Master Admin, the delete is converted into a delete-request routed
    // to Master Admin. Only Master Admin can perform the actual delete.
    const rec = records.find((r) => r.id === id) || {};
    const isApproved = String(rec.status || "").toLowerCase() === "approved";
    const isMaster = user?.role === "master_admin";
    if (isApproved && !isMaster) {
      const reason = window.prompt(
        `This record is APPROVED and cannot be deleted directly.\n\nEnter a reason to request deletion (Master Admin will review):`
      );
      const trimmed = (reason || "").trim();
      if (trimmed.length < 3) { toast.error("Reason (min 3 chars) required"); return; }
      try {
        await api.post("/edit-access-requests", {
          module_key: moduleKey,
          action: "delete",
          record_id: id,
          reason: trimmed,
        });
        toast.success("Delete request sent to Master Admin");
      } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
      return;
    }
    try {
      await api.delete(`/records/${moduleKey}/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    }
  };

  const [adminDateFilter, setAdminDateFilter] = useState("");
  const [adminEmpFilter, setAdminEmpFilter] = useState("all");
  const [adminSort, setAdminSort] = useState("date_desc");

  // v109.9.66 — IT Support Tickets: two-tab UX (My Raise Request / All Request)
  const [ticketTab, setTicketTab] = useState("mine");
  const filtered = useMemo(() => {
    let list = records;
    // Admin filters (date + user + sort) — used by hr_attendance
    if (config.adminFilters) {
      const { dateField, employeeField } = config.adminFilters;
      if (adminDateFilter) list = list.filter((r) => String(r[dateField] || "").slice(0, 10) === adminDateFilter);
      if (adminEmpFilter !== "all") list = list.filter((r) => r[employeeField] === adminEmpFilter);
      list = [...list].sort((a, b) => {
        if (adminSort === "date_desc") return String(b[dateField] || "").localeCompare(String(a[dateField] || ""));
        if (adminSort === "date_asc") return String(a[dateField] || "").localeCompare(String(b[dateField] || ""));
        if (adminSort === "user") return String(a[employeeField] || "").localeCompare(String(b[employeeField] || ""));
        return 0;
      });
    }
    // CRM Reporting filters
    if (moduleKey === "sales_crm_report") {
      if (leadFilter !== "all") list = list.filter((r) => r.lead_status === leadFilter);
      if (followFilter === "today") {
        const t = new Date().toISOString().slice(0, 10);
        list = list.filter((r) => r.next_follow_up === t);
      } else if (followFilter === "overdue") {
        const t = new Date().toISOString().slice(0, 10);
        list = list.filter((r) => r.next_follow_up && r.next_follow_up < t);
      } else if (followFilter === "upcoming") {
        const t = new Date().toISOString().slice(0, 10);
        list = list.filter((r) => r.next_follow_up && r.next_follow_up > t);
      }
    }
    if (moduleKey === "hr_daily_report") {
      const today = new Date().toISOString().slice(0, 10);
      if (dayFilter === "today") list = list.filter((r) => (r.report_date || "").slice(0, 10) === today);
      else if (dayFilter === "week") {
        const week = new Date(); week.setDate(week.getDate() - 6);
        const cutoff = week.toISOString().slice(0, 10);
        list = list.filter((r) => (r.report_date || "") >= cutoff);
      } else if (dayFilter === "custom" && customDate) {
        list = list.filter((r) => (r.report_date || "").slice(0, 10) === customDate);
      }
    }
    // v109.9.66 — IT Support Tickets: Mine (own raises) vs All Request
    if (moduleKey === "it_tickets") {
      const uid = user?.id;
      const uname = user?.name;
      const isMine = (r) => (r.created_by_id && uid && r.created_by_id === uid)
        || (r.created_by_name && uname && r.created_by_name === uname);
      if (ticketTab === "mine") list = list.filter(isMine);
      // "all" shows the full list (Master Access-gated).
    }
    const q = search.toLowerCase();
    if (!q) return list;
    return list.filter((r) =>
      Object.values(r).some((v) => String(v ?? "").toLowerCase().includes(q))
    );
  }, [records, search, leadFilter, followFilter, dayFilter, customDate, moduleKey, adminDateFilter, adminEmpFilter, adminSort, config.adminFilters, ticketTab, user]);

  // Recompute auto-days when from/to/half-day changes
  useEffect(() => {
    if (!config.fields.some((f) => f.autoDays)) return;
    if (form.is_half_day === "Yes") {
      // Half day: 0.5 day, single date, to_date mirrors from_date
      setForm((prev) => ({
        ...prev,
        to_date: prev.from_date,
        days_count: 0.5,
      }));
    } else {
      setForm((prev) => ({ ...prev, days_count: calcDays(prev.from_date, prev.to_date) }));
    }
  }, [form.from_date, form.to_date, form.is_half_day]);

  // v109.9.76 — module-admin flag now goes through Master Access effective
  // perms instead of legacy role names. "Admin" for a module == having edit
  // rights on that module (any tab or sub-tab level).
  const isAdminForMod = user?.role === "master_admin"
    || (moduleKey === "hr_daily_report"    && moduleCanDo(user, "hr_daily_report", "edit"))
    || (moduleKey === "hr_leaves"          && moduleCanDo(user, "hr_leaves", "edit"))
    || (moduleKey === "accounts_advances"  && moduleCanDo(user, "travel", "edit"))
    || (moduleKey === "accounts_claims"    && moduleCanDo(user, "travel", "edit"));

  // Records locked for the author after submission (only admins can modify)
  const LOCKED_MODULES = ["hr_daily_report", "accounts_advances", "accounts_claims"];
  // Fully read-only modules — NO ONE (including admins) can edit/delete via Records UI.
  // Attendance corrections must go through the Team Attendance audit-logged flow.
  const FULLY_READONLY_MODULES = ["hr_attendance"];
  const isLockedModule = LOCKED_MODULES.includes(moduleKey);
  const isFullyReadOnly = FULLY_READONLY_MODULES.includes(moduleKey);
  const canModifyRow = (rec) => {
    if (isFullyReadOnly) return false;
    if (!isLockedModule) {
      // Approved leaves are locked for non-admins. HR Admin / Master Admin
      // can still edit or delete them (to correct mistakes).
      if (moduleKey === "hr_leaves" && (rec?.status || "") === "Approved" && !isAdminForMod) {
        return false;
      }
      return true;
    }
    return isAdminForMod;
  };

  const actionsBar = (
          <div className="flex items-center gap-2">
            <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={openCreate} className="bg-gradient-to-br from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800 text-white h-10 shadow-sm" data-testid="record-add-button" disabled={isFullyReadOnly} style={isFullyReadOnly ? { display: "none" } : undefined}>
                <Plus size={16} strokeWidth={1.75} className="mr-1.5" /> New Record
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="heading">{editingId ? "Edit" : "New"} {config.label}</DialogTitle>
              </DialogHeader>
            {moduleKey === "hr_leaves" && <LeaveBalanceBanner form={form} />}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
              {config.fields.map((f) => {
                // showIf: { field: <key>, value: <required value> } OR { field: <key>, notValue: <forbidden value> }
                if (f.showIf) {
                  const cur = form[f.showIf.field];
                  if (f.showIf.value !== undefined && cur !== f.showIf.value) return null;
                  if (f.showIf.notValue !== undefined && cur === f.showIf.notValue) return null;
                }
                // dynamicLabel: { whenField, whenValue, labelYes, labelNo }
                let label = f.label;
                if (f.dynamicLabel) {
                  const dl = f.dynamicLabel;
                  label = form[dl.whenField] === dl.whenValue ? dl.labelYes : dl.labelNo;
                }
                const lockedByRole = f.adminOnly && !isAdminForMod;
                const fieldReadOnly = f.readOnly || lockedByRole;
                return (
                <div key={f.key} className={f.type === "textarea" ? "md:col-span-2" : ""}>
                  <Label className="text-[12px] uppercase tracking-wider font-semibold text-slate-600">
                    {label}{f.required ? " *" : ""}
                  </Label>
                  <div className="mt-1.5">
                    {f.type === "select" ? (
                      <Select value={form[f.key] || ""} onValueChange={(v) => setForm({ ...form, [f.key]: v })} disabled={fieldReadOnly}>
                        <SelectTrigger data-testid={`field-${f.key}`}><SelectValue placeholder="Select…" /></SelectTrigger>
                        <SelectContent>
                          {f.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    ) : f.type === "textarea" ? (
                      <Textarea
                        value={form[f.key] || ""}
                        onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                        rows={3}
                        readOnly={fieldReadOnly}
                        className={fieldReadOnly ? "bg-slate-50 text-slate-600" : ""}
                        data-testid={`field-${f.key}`}
                      />
                    ) : (
                      <Input
                        type={f.type === "date" ? "date" : "text"}
                        value={form[f.key] || ""}
                        onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                        placeholder={f.placeholder || ""}
                        readOnly={fieldReadOnly}
                        className={fieldReadOnly ? "bg-slate-50 text-slate-600 cursor-not-allowed" : ""}
                        data-testid={`field-${f.key}`}
                      />
                    )}
                  </div>
                </div>
                );
              })}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={submit} disabled={loading} className="bg-[var(--pk-accent)] hover:bg-[var(--pk-accent-hover)] text-white" data-testid="record-submit">
                {loading ? "Saving…" : editingId ? "Save Changes" : "Create"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
          </div>
        );

  return (
    <div className={embedded ? "space-y-4" : "max-w-[1400px] space-y-6"}>
      {!embedded ? (
        <PageHeader
          tone={MODULE_TONE_MAP[config.group] || "blue"}
          icon={config.icon}
          eyebrow={`${config.group} Module`}
          title={config.label}
          moduleKey={moduleKey}
          subtitle={
            isFullyReadOnly
              ? "Attendance records are read-only. Use Team Attendance to correct status."
              : isLockedModule
              ? "Submit your request. Once submitted, only Admin can edit or delete."
              : `Manage ${config.label.toLowerCase()} records.`
          }
          actions={actionsBar}
        />
      ) : (
        <div className="flex items-center justify-end">{actionsBar}</div>
      )}

      <div className="flex items-center gap-2 max-w-3xl flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search…" className="pl-9 h-10" data-testid="record-search" />
        </div>
        {config.adminFilters && isAdminForMod && (
          <>
            <Input
              type="date" value={adminDateFilter}
              onChange={(e) => setAdminDateFilter(e.target.value)}
              className="h-10 w-40 mono text-xs" placeholder="Date"
              data-testid="filter-admin-date"
            />
            <Select value={adminEmpFilter} onValueChange={setAdminEmpFilter}>
              <SelectTrigger className="h-10 w-48" data-testid="filter-admin-employee"><SelectValue placeholder="Employee" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Employees</SelectItem>
                {Array.from(new Set(records.map((r) => r[config.adminFilters.employeeField]).filter(Boolean))).sort().map((n) => (
                  <SelectItem key={n} value={n}>{n}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={adminSort} onValueChange={setAdminSort}>
              <SelectTrigger className="h-10 w-36" data-testid="filter-admin-sort"><SelectValue placeholder="Sort" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="date_desc">Date ↓</SelectItem>
                <SelectItem value="date_asc">Date ↑</SelectItem>
                <SelectItem value="user">User A–Z</SelectItem>
              </SelectContent>
            </Select>
            {(adminDateFilter || adminEmpFilter !== "all") && (
              <Button size="sm" variant="outline" onClick={() => { setAdminDateFilter(""); setAdminEmpFilter("all"); }} className="h-10 text-xs" data-testid="filter-admin-clear">Clear</Button>
            )}
          </>
        )}
        {moduleKey === "hr_daily_report" && (
          <>
            <Select value={dayFilter} onValueChange={setDayFilter}>
              <SelectTrigger className="h-10 w-40" data-testid="filter-day"><SelectValue placeholder="Day" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Reports</SelectItem>
                <SelectItem value="today">Today</SelectItem>
                <SelectItem value="week">Past 7 days</SelectItem>
                <SelectItem value="custom">Specific Date…</SelectItem>
              </SelectContent>
            </Select>
            {dayFilter === "custom" && (
              <Input type="date" value={customDate} onChange={(e) => setCustomDate(e.target.value)} className="h-10 w-40 mono text-xs" data-testid="filter-day-custom" />
            )}
          </>
        )}
        {moduleKey === "sales_crm_report" && (
          <>
            <Select value={leadFilter} onValueChange={setLeadFilter}>
              <SelectTrigger className="h-10 w-44" data-testid="filter-lead-status"><SelectValue placeholder="Lead Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Lead Status</SelectItem>
                <SelectItem value="HOT">HOT</SelectItem>
                <SelectItem value="Converted to Sales">Converted to Sales</SelectItem>
                <SelectItem value="Cold">Cold</SelectItem>
                <SelectItem value="Not Interested">Not Interested</SelectItem>
                <SelectItem value="SPAM">SPAM</SelectItem>
              </SelectContent>
            </Select>
            <Select value={followFilter} onValueChange={setFollowFilter}>
              <SelectTrigger className="h-10 w-44" data-testid="filter-followup"><SelectValue placeholder="Follow-Up" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Follow-Ups</SelectItem>
                <SelectItem value="today">Due Today</SelectItem>
                <SelectItem value="overdue">Overdue</SelectItem>
                <SelectItem value="upcoming">Upcoming</SelectItem>
              </SelectContent>
            </Select>
          </>
        )}
      </div>

      {/* v109.9.66 — Support Tickets: My Raise / All Request tab strip */}
      {moduleKey === "it_tickets" && (
        <div className="flex items-center gap-1 mb-3" data-testid="it-tickets-tabs">
          {[
            { key: "mine", label: "My Raise Request" },
            { key: "all",  label: "All Request" },
          ].map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTicketTab(t.key)}
              className={`px-3 py-1.5 rounded-full text-[12px] font-bold transition-all ${ticketTab === t.key
                  ? "bg-indigo-600 text-white shadow-md shadow-indigo-500/20"
                  : "bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-700 hover:border-indigo-300"}`}
              data-testid={`it-tickets-tab-${t.key}`}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}

      {(() => {
        const tone = MODULE_TONE_MAP[config.group] || "blue";
        const TONE_TABLE = {
          blue:    { border: "border-blue-200",    bg: "from-blue-50/40 via-white to-indigo-50/30",     blob: "bg-blue-200/30",    th: "text-blue-700",    hdr: "border-blue-200 bg-blue-50/60",      row: "hover:bg-blue-50/40 border-blue-100/60" },
          indigo:  { border: "border-indigo-200",  bg: "from-indigo-50/40 via-white to-violet-50/30",   blob: "bg-indigo-200/30",  th: "text-indigo-700",  hdr: "border-indigo-200 bg-indigo-50/60",  row: "hover:bg-indigo-50/40 border-indigo-100/60" },
          violet:  { border: "border-violet-200",  bg: "from-violet-50/40 via-white to-fuchsia-50/30",  blob: "bg-violet-200/30",  th: "text-violet-700",  hdr: "border-violet-200 bg-violet-50/60",  row: "hover:bg-violet-50/40 border-violet-100/60" },
          sky:     { border: "border-sky-200",     bg: "from-sky-50/40 via-white to-cyan-50/30",        blob: "bg-sky-200/30",     th: "text-sky-700",     hdr: "border-sky-200 bg-sky-50/60",         row: "hover:bg-sky-50/40 border-sky-100/60" },
          emerald: { border: "border-emerald-200", bg: "from-emerald-50/40 via-white to-teal-50/30",    blob: "bg-emerald-200/30", th: "text-emerald-700", hdr: "border-emerald-200 bg-emerald-50/60", row: "hover:bg-emerald-50/40 border-emerald-100/60" },
          amber:   { border: "border-amber-200",   bg: "from-amber-50/40 via-white to-orange-50/30",    blob: "bg-amber-200/30",   th: "text-amber-700",   hdr: "border-amber-200 bg-amber-50/60",    row: "hover:bg-amber-50/40 border-amber-100/60" },
        };
        const t = TONE_TABLE[tone] || TONE_TABLE.blue;
        // v109.7 — For explicit-grant modules that support the global view
        // toggle, render as vertical cards when Card mode is active. Legacy
        // `config.verticalCards` still forces cards regardless of toggle.
        const asCards = config.verticalCards || (supportsViewToggle && viewMode === "card");
        // v109.7.1 — Support Tickets has a purpose-built ticket-card layout
        // (priority-tinted left rail, status pill, category / priority chips,
        // description clamp, assignee + created-by / date meta row).
        const asTicketCards = moduleKey === "it_tickets" && supportsViewToggle && viewMode === "card";
        return asTicketCards ? (
          <TicketCardGrid
            filtered={filtered}
            canModifyRow={canModifyRow}
            openEdit={openEdit}
            remove={remove}
            t={t}
          />
        ) : asCards ? (
          <div className="space-y-3" data-testid="vertical-cards">
            {filtered.length === 0 && (
              <div className={`relative overflow-hidden rounded-xl border ${t.border} bg-gradient-to-br ${t.bg} p-12 text-center text-slate-400 text-sm`}>
                <div className={`absolute -top-10 -right-10 w-32 h-32 ${t.blob} rounded-full blur-3xl pointer-events-none`} />
                <div className="relative">No records yet.</div>
              </div>
            )}
            {filtered.map((r) => (
              <div key={r.id} className={`relative overflow-hidden rounded-xl border ${t.border} bg-gradient-to-br ${t.bg} p-5 group hover:-translate-y-0.5 hover:shadow-lg transition-all`} data-testid={`record-card-${r.id}`}>
                <div className={`absolute -top-10 -right-10 w-32 h-32 ${t.blob} rounded-full blur-3xl pointer-events-none`} />
                <div className="relative">
                  <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      {r.report_date && <span className="mono text-[12px] font-bold text-slate-900">{r.report_date}</span>}
                      {r.report_time && <span className="mono text-[11px] text-slate-500">{r.report_time}</span>}
                      {r.created_by_name && <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-white/80 border ${t.border} ${t.th}`}>{r.created_by_name}</span>}
                    </div>
                    {canModifyRow(r) && (
                      <div className="flex gap-1.5">
                        <Button size="sm" variant="outline" className="h-7 text-[11px] bg-white" onClick={() => openEdit(r)} data-testid={`record-edit-${r.id}`}>
                          <Pencil size={11} className="mr-1" /> Edit
                        </Button>
                        <Button size="sm" variant="outline" className="h-7 text-[11px] text-rose-600 border-rose-200 hover:bg-rose-50" onClick={() => remove(r.id)} data-testid={`record-delete-${r.id}`}>
                          <Trash2 size={11} />
                        </Button>
                      </div>
                    )}
                  </div>
                  <div className="space-y-2.5">
                    {(config.fields || []).filter((f) => !["employee_name", "report_date", "report_time"].includes(f.key) && !f.autoFromUser).map((f) => (
                      <div key={f.key}>
                        <div className={`text-[10px] uppercase tracking-[0.16em] font-bold ${t.th}`}>{f.label}</div>
                        <div className="text-sm text-slate-800 mt-0.5 whitespace-pre-wrap break-words" data-testid={`record-${r.id}-${f.key}`}>
                          {String(r[f.key] ?? "").trim() || <span className="text-slate-300 italic">—</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className={`relative overflow-hidden rounded-xl border ${t.border} bg-gradient-to-br ${t.bg} ${config.noHorizontalScroll ? "overflow-x-hidden" : "overflow-x-auto"}`}>
            <div className={`absolute -top-10 -right-10 w-32 h-32 ${t.blob} rounded-full blur-3xl pointer-events-none`} />
            <table className="relative w-full text-sm responsive-table">
              <thead className={`border-b ${t.hdr}`}>
                <tr>
                  {config.columns.map((c) => <th key={c} className={`px-4 py-3 text-left text-[10px] uppercase tracking-[0.16em] font-bold ${t.th} ${config.noHorizontalScroll ? "" : "whitespace-nowrap"}`}>{labelFor(c, config.fields, config.columnLabels)}</th>)}
                  <th className={`px-4 py-3 text-right text-[10px] uppercase tracking-[0.16em] font-bold ${t.th}`}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr><td colSpan={config.columns.length + 1} className="text-center py-12 text-slate-400 text-sm">No records yet. Click &quot;New Record&quot; to add one.</td></tr>
                )}
                {filtered.map((r) => (
                  <tr key={r.id} className={`border-b ${t.row} transition-colors`} data-testid={`record-row-${r.id}`}>
                {config.columns.map((c) => (
                  <td key={c} className={`px-4 py-3 text-slate-700 ${config.verticalLayout ? "align-top" : "whitespace-nowrap"}`} data-label={labelFor(c, config.fields, config.columnLabels)}>
                    {renderCellFormatted(c, r, config)}
                  </td>
                ))}
                <td className="px-4 py-3 text-right" data-label="Actions">
                  <div className="flex items-center justify-end gap-2">
                    {moduleKey === "accounts_advances" && (
                      <TravelAdvanceActions row={r} onChanged={load} />
                    )}
                    {canModifyRow(r) ? (
                      <>
                        <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => openEdit(r)} data-testid={`record-edit-${r.id}`}>
                          <Pencil size={12} className="mr-1" /> Edit
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button size="sm" variant="outline" className="h-8 text-xs text-red-600 border-red-200 hover:bg-red-50" data-testid={`record-delete-${r.id}`}>
                              <Trash2 size={12} />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete record?</AlertDialogTitle>
                              <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => remove(r.id)} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </>
                    ) : (
                      <span className="text-[11px] text-slate-400 italic" data-testid={`record-locked-${r.id}`}>{isFullyReadOnly ? "Read-only" : "Locked"}</span>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
        );
      })()}
      {/* v109.9.7 — Save-and-request dialog (auto-opens on 403 for edits) */}
      {editReqDialog}
    </div>
  );
}

function labelFor(key, fields, columnLabels) {
  if (columnLabels && columnLabels[key]) return columnLabels[key];
  if (key === "created_by_name") return "Created By";
  const f = fields.find((x) => x.key === key);
  return f?.label || key;
}

function renderCellFormatted(key, row, config) {
  const fmtConfig = config.columnFormatters?.[key];
  const value = row[key];
  if (!fmtConfig) {
    return renderCell(key, value, config.fields);
  }
  if (fmtConfig.type === "datetime_ist") {
    if (!value) return <span className="text-slate-300">—</span>;
    try {
      const d = new Date(value);
      if (isNaN(d.getTime())) return String(value);
      return (
        <div className="text-[12px] mono">
          {d.toLocaleString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: true })}
          <div className="text-[9px] text-slate-400">IST · GMT+5:30</div>
        </div>
      );
    } catch { return String(value); }
  }
  if (fmtConfig.type === "ip_geo") {
    if (!value) return <span className="text-slate-300">—</span>;
    const geo = row[fmtConfig.geoKey] || {};
    const place = [geo.city, geo.region, geo.country].filter(Boolean).join(", ");
    return (
      <div className="text-[12px]">
        <div className="mono font-semibold text-slate-800">{value}</div>
        {place ? <div className="text-[10px] text-slate-600">📍 {place}</div> : <div className="text-[10px] text-slate-400 italic">Location lookup pending</div>}
      </div>
    );
  }
  if (fmtConfig.type === "attendance_punch") {
    const time = row[fmtConfig.timeKey];
    const loc = row[fmtConfig.locationKey];
    const ip = row[fmtConfig.ipKey];
    const geo = row[fmtConfig.geoKey] || {};
    if (!time && !ip && !loc) return <span className="text-slate-300">—</span>;
    let timeStr = "—";
    if (time) {
      try {
        const d = new Date(time);
        timeStr = isNaN(d.getTime())
          ? String(time)
          : d.toLocaleString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: true });
      } catch { timeStr = String(time); }
    }
    const place = [geo.city, geo.region, geo.country].filter(Boolean).join(", ");
    return (
      <div className="text-[12px] space-y-0.5 min-w-0">
        <div className="font-semibold text-slate-800">{timeStr} <span className="text-[9px] text-slate-400 font-normal">IST</span></div>
        {loc && <div className="text-[11px] text-violet-700 font-medium">📍 {loc}</div>}
        {ip && <div className="mono text-[10px] text-slate-600 break-all">{ip}</div>}
        {place && <div className="text-[10px] text-slate-500 italic">{place}</div>}
      </div>
    );
  }
  return renderCell(key, value, config.fields);
}

function renderCell(key, value, fields) {
  if (value == null || value === "") return <span className="text-slate-300">—</span>;
  const f = fields.find((x) => x.key === key);
  if (f?.type === "select" && /status|priority/i.test(key)) {
    return <Badge variant="secondary" className={badgeColor(value)}>{value}</Badge>;
  }
  if (f?.type === "date") {
    // Backend stores date-only fields as 'YYYY-MM-DD' strings. Render as DD/MM/YYYY
    // directly so we don't accidentally shift the date by the browser's / server's
    // timezone (a bare 'YYYY-MM-DD' parses to UTC midnight which is 05:30 AM the
    // *same* day in IST, so this is safe — but string-slicing avoids Date parsing
    // entirely).
    const s = String(value);
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return `${m[3]}/${m[2]}/${m[1]}`;
    // Fallback: parse then format
    const d = new Date(s);
    if (!isNaN(d)) return d.toLocaleDateString("en-GB");
    return s;
  }
  if (f?.type === "datetime") {
    const d = new Date(value);
    if (!isNaN(d)) {
      return d.toLocaleString("en-GB", {
        day: "2-digit", month: "2-digit", year: "numeric",
        hour: "2-digit", minute: "2-digit", hour12: true,
      });
    }
  }
  return String(value);
}

function badgeColor(v) {
  const s = String(v).toLowerCase();
  if (/(present|approved|paid|delivered|active|resolved|disbursed|settled|confirmed)/.test(s))
    return "bg-green-100 text-green-700 hover:bg-green-100";
  if (/(absent|rejected|cancelled|overdue|retired|returned|critical)/.test(s))
    return "bg-red-100 text-red-700 hover:bg-red-100";
  if (/(pending|draft|requested|open|in.progress|in.transit|processing|prospect|lead|sent|half day|on leave|in repair|high)/.test(s))
    return "bg-amber-100 text-amber-800 hover:bg-amber-100";
  return "bg-slate-100 text-slate-700 hover:bg-slate-100";
}


function LeaveBalanceBanner({ form }) {
  const [bal, setBal] = useState(null);
  useEffect(() => { api.get("/my-leave-balance").then(({ data }) => setBal(data)).catch(() => {}); }, []);
  if (!bal) return null;
  const sel = (form.leave_type || "").toLowerCase();
  const key = sel.includes("casual") ? "casual" : sel.includes("sick") ? "sick" : sel.includes("earned") ? "earned" : sel.includes("unpaid") ? "unpaid" : null;
  const isHalf = form.is_half_day === "Yes";
  // Days requested from form (half-day overrides to 0.5)
  let req = 0;
  if (isHalf) {
    req = 0.5;
  } else if (form.from_date && form.to_date) {
    try {
      const start = new Date(form.from_date + "T00:00:00");
      const end = new Date(form.to_date + "T00:00:00");
      const ms = end - start;
      if (ms >= 0) req = Math.floor(ms / 86400000) + 1;
    } catch { /* */ }
  }
  const avail = key && key !== "unpaid" ? Number(bal[key] || 0) : null;
  // Insufficient logic applies to BOTH full leaves and half-day leaves — only Unpaid bypasses it.
  const insufficient = key && key !== "unpaid" && avail !== null && req > avail;

  return (
    <div className="mb-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]" data-testid="leave-balance-banner">
      {["casual", "sick", "earned", "comp_off"].map((k) => (
        <div
          key={k}
          className={`border-2 rounded-md p-2 ${key === k ? "border-violet-500 bg-violet-50" : "border-slate-200 bg-slate-50/50"}`}
          data-testid={`leave-balance-${k}`}
        >
          <div className="text-[9px] uppercase tracking-wider font-semibold text-slate-500">
            {k === "comp_off" ? "Comp Off" : k.charAt(0).toUpperCase() + k.slice(1)}
          </div>
          <div className="text-lg font-extrabold text-slate-800 mono">{bal[k] || 0}</div>
        </div>
      ))}
      {sel === "unpaid" && (
        <div className="md:col-span-4 bg-slate-100 border border-slate-300 rounded-md px-3 py-2 text-[12px] text-slate-700" data-testid="leave-unpaid-notice">
          <strong>Unpaid Leave</strong> — does not deduct any balance but counted in payroll.
        </div>
      )}
      {insufficient && (
        <div className="md:col-span-4 bg-rose-50 border-2 border-rose-300 rounded-md px-3 py-2 text-[12px] text-rose-800" data-testid="leave-insufficient">
          <strong>⚠ Insufficient balance.</strong> You have <strong>{avail}</strong> day(s) of {key} leave but requested <strong>{req}</strong> day(s). Please reduce the range OR submit as <strong>Unpaid</strong>.
        </div>
      )}
    </div>
  );
}

// v109.7.1 — Purpose-built Support Ticket card layout used when the global
// view mode is "card" and moduleKey === "it_tickets".
const TICKET_PRIORITY = {
  Critical: { bar: "bg-rose-600",   pill: "bg-rose-100 text-rose-700 ring-rose-300",   dot: "bg-rose-500",  glow: "from-rose-100/60" },
  High:     { bar: "bg-orange-500", pill: "bg-orange-100 text-orange-700 ring-orange-300", dot: "bg-orange-500", glow: "from-orange-100/60" },
  Medium:   { bar: "bg-amber-400",  pill: "bg-amber-100 text-amber-800 ring-amber-300", dot: "bg-amber-500",  glow: "from-amber-100/60" },
  Low:      { bar: "bg-sky-400",    pill: "bg-sky-100 text-sky-700 ring-sky-300",       dot: "bg-sky-500",    glow: "from-sky-100/60" },
};
const TICKET_STATUS = {
  Open:          { pill: "bg-blue-100 text-blue-700 ring-blue-300",       icon: "○" },
  "In Progress": { pill: "bg-indigo-100 text-indigo-700 ring-indigo-300", icon: "◐" },
  Resolved:      { pill: "bg-emerald-100 text-emerald-700 ring-emerald-300", icon: "✓" },
  Closed:        { pill: "bg-slate-200 text-slate-700 ring-slate-300",    icon: "✕" },
};
const TICKET_CATEGORY = {
  Hardware: "bg-purple-50 text-purple-700 ring-purple-200",
  Software: "bg-blue-50 text-blue-700 ring-blue-200",
  Network:  "bg-cyan-50 text-cyan-700 ring-cyan-200",
  Access:   "bg-fuchsia-50 text-fuchsia-700 ring-fuchsia-200",
  Other:    "bg-slate-50 text-slate-700 ring-slate-200",
};

function TicketCardGrid({ filtered, canModifyRow, openEdit, remove }) {
  if (filtered.length === 0) {
    return (
      <div className="relative overflow-hidden rounded-xl border border-blue-200 bg-gradient-to-br from-blue-50/40 via-white to-indigo-50/30 p-12 text-center text-slate-400 text-sm">
        <div className="absolute -top-10 -right-10 w-32 h-32 bg-blue-200/30 rounded-full blur-3xl pointer-events-none" />
        <div className="relative">No tickets yet.</div>
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="ticket-card-grid">
      {filtered.map((r) => {
        const pri = TICKET_PRIORITY[r.priority] || TICKET_PRIORITY.Low;
        const sta = TICKET_STATUS[r.status] || TICKET_STATUS.Open;
        const cat = TICKET_CATEGORY[r.category] || TICKET_CATEGORY.Other;
        const created = r.created_at ? String(r.created_at).slice(0, 10) : (r.report_date || "");
        return (
          <div
            key={r.id}
            className="relative overflow-hidden rounded-xl border border-slate-200 bg-white hover:shadow-lg hover:-translate-y-0.5 transition-all group"
            data-testid={`ticket-card-${r.id}`}
          >
            <div className={`absolute top-0 bottom-0 left-0 w-1.5 ${pri.bar}`} />
            <div className={`absolute -top-12 -right-12 w-40 h-40 rounded-full blur-3xl pointer-events-none bg-gradient-to-br ${pri.glow} to-transparent`} />

            <div className="relative pl-5 pr-4 py-4">
              <div className="flex items-start justify-between gap-3 mb-2.5">
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full ring-1 text-[10.5px] font-bold uppercase tracking-wider ${sta.pill}`}>
                  <span className="text-[11px] leading-none">{sta.icon}</span>
                  {r.status || "Open"}
                </span>
                {canModifyRow(r) && (
                  <div className="flex gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button size="sm" variant="outline" className="h-7 text-[11px] bg-white" onClick={() => openEdit(r)} data-testid={`ticket-card-edit-${r.id}`}>
                      <Pencil size={11} className="mr-1" /> Edit
                    </Button>
                    <Button size="sm" variant="outline" className="h-7 text-[11px] text-rose-600 border-rose-200 hover:bg-rose-50" onClick={() => remove(r.id)} data-testid={`ticket-card-delete-${r.id}`}>
                      <Trash2 size={11} />
                    </Button>
                  </div>
                )}
              </div>

              <div className="text-[14.5px] font-extrabold text-slate-900 leading-snug mb-1.5 line-clamp-2" title={r.title}>
                {r.title || <span className="text-slate-400 italic">Untitled ticket</span>}
              </div>

              <div className="flex items-center gap-1.5 flex-wrap mb-2.5">
                {r.category && (
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-md ring-1 text-[10.5px] font-semibold ${cat}`}>{r.category}</span>
                )}
                {r.priority && (
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md ring-1 text-[10.5px] font-semibold ${pri.pill}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${pri.dot}`} />
                    {r.priority}
                  </span>
                )}
              </div>

              <div className="text-[12px] text-slate-600 leading-relaxed line-clamp-3 mb-3 whitespace-pre-wrap">
                {(r.description || "").trim() || <span className="text-slate-300 italic">No description provided.</span>}
              </div>

              <div className="pt-2.5 border-t border-slate-100 grid grid-cols-2 gap-x-3 gap-y-1.5 text-[10.5px]">
                <div className="min-w-0">
                  <div className="uppercase tracking-wider font-bold text-slate-400 text-[9px]">Raised by</div>
                  <div className="text-slate-800 font-semibold truncate">{r.created_by_name || <span className="text-slate-400 italic">—</span>}</div>
                </div>
                <div className="min-w-0">
                  <div className="uppercase tracking-wider font-bold text-slate-400 text-[9px]">Assigned to (IT)</div>
                  <div className="text-slate-800 font-semibold truncate">{r.assigned_to || <span className="text-slate-400 italic">Unassigned</span>}</div>
                </div>
                {created && (
                  <div className="col-span-2 pt-0.5 flex items-center gap-1 text-slate-500">
                    <span className="uppercase tracking-wider font-bold text-slate-400 text-[9px]">Created</span>
                    <span className="mono text-slate-700">{created}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

